import { type Configuration, LogLevel } from "@azure/msal-browser";

// EntraID (Azure AD) config for the Admin identity plane. Values come from VITE_IDP_* env (the
// client id + tenant are public, not secrets). Top-level redirect flows return to appRedirectUri;
// popup and silent flows use a dedicated bridge page so that page never boots the application.
const tenantId = import.meta.env.VITE_IDP_TENANT_ID ?? "";
const clientId = import.meta.env.VITE_IDP_CLIENT_ID ?? "";
export const appRedirectUri = import.meta.env.VITE_IDP_REDIRECT_URI || window.location.origin;
export const authBridgeUri = new URL("/redirect.html", window.location.origin).href;
const popupRelayUri = new URL("/relay.html?v=1", window.location.origin).href;
export const framedAuth = window.self !== window.top;

/** Auth wires up only when a tenant + client id are present; otherwise the app runs anonymous-only. */
export const authEnabled = Boolean(tenantId && clientId);

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: authBridgeUri,
    postLogoutRedirectUri: appRedirectUri,
    ...(framedAuth ? { popupRelayUri } : {}),
  },
  // localStorage (not sessionStorage) so the admin session is shared across tabs: the Content
  // Review screen opens experiences/engagements in new tabs, which must stay signed in. Trade-off:
  // tokens persist on the origin until sign-out/expiry rather than clearing when the tab closes —
  // acceptable for this single-admin site.
  cache: { cacheLocation: "localStorage" },
  system: {
    popupBridgeTimeout: 125_000,
    loggerOptions: {
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level === LogLevel.Error) console.error("[msal]", message);
      },
    },
  },
};

// Custom API scope exposed on the same app registration — the access token the admin write API
// (HTTP API JWT authorizer) validates.
export const apiScope = clientId ? `api://${clientId}/access_as_user` : "";
export const apiTokenRequest = { scopes: apiScope ? [apiScope] : [] };

// Request the API scope at sign-in too (not just openid/profile/User.Read) so the admin consents to
// it once at login. Otherwise the first getApiToken() falls back to an interactive popup that the
// browser blocks (it fires after an await), and every admin write (config save, content edits,
// the Connect picker) silently fails with no token ever sent.
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read", ...(apiScope ? [apiScope] : [])],
};
